<script setup lang="ts">
import useVuelidate from "@vuelidate/core"
import { email, required } from "@vuelidate/validators"

import TextInput from "@/components/Forms/TextInput.vue"
import ErrorMessage from "@/components/Forms/ErrorMessage.vue"
import EmailInput from "@/components/Forms/EmailInput.vue"

import { nextTick, reactive } from "vue"
import { Activiteit } from "@/repositories/activiteit.repository"
import WidgetRepository, { WidgetFormRequestModel } from "@/repositories/widget.repository"
import { nl as nlLocale } from "date-fns/locale"
import { getFormattedTime } from "@/helpers/timezone.helper";
import WidgetBoekenResult from "./WidgetBoekenResult.vue";
import { SportmatchWidgetCmsData } from "@/view-models/sportmatchwidget.model";

const props = defineProps<{
    activiteit: Activiteit
    platform: number
    cmsData: {
        successTitle: string;
        successMessage: string;
        errorTitle: string;
        errorMessage: string;
        successImage?: string;
    }
}>()

const state = reactive({
    firstName: '',
    lastName: '',
    email: '',
    submitted: false,
    loading: false,
    success: false,
    loaded: false
})

const rules = {
    firstName: {
        required,
    },
    lastName: {
        required,
    },
    email: {
        email,
        required,
    },
}

const v$ = useVuelidate(rules, state)

const submit = async () => {
    state.submitted = true
    const isFormCorrect = await v$.value.$validate()

    if (isFormCorrect) {
        state.loading = true
        try {
            const response = await WidgetRepository.submit({
                firstName: state.firstName,
                lastName: state.lastName,
                email: state.email,
                platform: props.platform,
                sportMoment: props.activiteit.SportmomentId
            })

            state.success = response;
            state.loaded = true;
        } catch (e) {
            state.success = false;
        } finally {
            state.loading = false
        }



        // Make cool animation??
    } else {
        await nextTick(() => {
            document.querySelector(".invalid-feedback")?.scrollIntoView({ block: "center", behavior: "smooth" })
        })
    }
}

const reset = () => {
    state.loaded = false;
    state.submitted = false
}

</script>

<template>
    <WidgetBoekenResult v-if="state.loaded" :success-title="cmsData.successTitle"
        :success-message="cmsData.successMessage" :error-title="cmsData.errorTitle"
        :error-message="cmsData.errorMessage" :success="state.success" :image="cmsData.successImage" @reset="reset" />
    <div v-else class="sportmatch-widget-panel-boeken">
        <div class="sportmatch-widget-panel-boeken-header">
            <h2 class="h3">Wat ga ik doen</h2>
            <p class="mb-0">Je gaat nu een activiteit reserveren voor:</p>
            <h3 class="h5">{{ activiteit.Naam }}</h3>
            <ul class="sportmatch-widget-panel-boeken-header-details">
                <li>
                    <div>
                        <span class="icon icon-agenda" aria-hidden="true"></span>{{
                            getFormattedTime(activiteit.BeginDatum, 'dd-MM-yyyy', nlLocale) }}
                    </div>
                    <div>
                        <span class="icon icon-clock" aria-hidden="true"></span>{{
                            getFormattedTime(activiteit.BeginDatum, 'HH:mm', nlLocale) }} - {{
                            getFormattedTime(activiteit.EindDatum, 'HH:mm', nlLocale) }}uur
                    </div>
                </li>
                <li>
                    <div>
                        <span class="icon icon-map-pin"></span>{{ activiteit.LocatieItem.Naam }}, {{
                            activiteit.LocatieItem.Adres }}, {{ activiteit.LocatieItem.Woonplaats }}
                    </div>
                </li>
                <li v-if="activiteit.BetalingsType == 'Credits'">
                    <div><span class="icon icon-euro"></span>1 credit</div>
                </li>
                <li v-if="activiteit.BetalingsType == 'Gratis'">
                    <div><span class="icon icon-euro"></span>Gratis</div>
                </li>
                <li v-if="activiteit.BetalingsType == 'Betaling'">
                    <div><span class="icon icon-euro"></span>{{ activiteit.Kosten }}</div>
                </li>
            </ul>
        </div>
        <hr>
        <div class="sportmatch-widget-panel-boeken-form">
            <h3 class="h5">Maak je reservering af</h3>
            <form class="sportmatch-widget-panel-boeken-form-checkout" @submit.prevent="submit">
                <TextInput id="form-first-name" v-model="state.firstName"
                    class="sportmatch-widget-panel-boeken-form-group" label="Voornaam" :validation="{
                        model: v$.firstName.$error,
                        message: 'Dit veld is verplicht',
                        submitted: state.submitted,
                    }" :is-disabled="state.loading">
                    <template #label="{ isInvalid }">
                        <label :class="{ 'text-danger': isInvalid }" for="form-first-name">
                            Voornaam<span class="text-danger">*</span>
                        </label>
                    </template>
                    <template #input="{ isInvalid }">
                        <input id="form-first-name" v-model="state.firstName" class="form-control"
                            :class="{ 'is-invalid': isInvalid }" type="text" placeholder="Wat is jouw voornaam?"
                            :disabled="state.loading" />

                    </template>
                    <template #error="{ isInvalid }">
                        <ErrorMessage v-if="isInvalid && state.submitted && state.firstName === ''" class="text-danger"
                            message="Dit veld is verplicht" />
                    </template>
                </TextInput>

                <TextInput id="form-last-name" v-model="state.lastName"
                    class="sportmatch-widget-panel-boeken-form-group" :validation="{
                        model: v$.lastName.$error,
                        message: 'Dit veld is verplicht',
                        submitted: state.submitted,
                    }" :is-disabled="state.loading">
                    <template #label="{ isInvalid }">
                        <label :class="{ 'text-danger': isInvalid }" for="form-last-name">
                            Achternaam<span class="text-danger">*</span>
                        </label>
                    </template>
                    <template #input="{ isInvalid }">
                        <input id="form-last-name" v-model="state.lastName" class="form-control"
                            :class="{ 'is-invalid': isInvalid }" type="text" placeholder="Wat is jouw achternaam?"
                            :disabled="state.loading" />

                    </template>
                    <template #error="{ isInvalid }">
                        <ErrorMessage v-if="isInvalid && state.submitted && state.lastName === ''" class="text-danger"
                            message="Dit veld is verplicht" />
                    </template>
                </TextInput>

                <EmailInput id="form-email" v-model="state.email" class="sportmatch-widget-panel-boeken-form-group"
                    label="E-Mailadres" :validation="{
                        model: v$.email.$error,
                        message: 'Dit veld is verplicht',
                        submitted: state.submitted,
                        invalid: [
                            {
                                value: v$.email.email.$invalid,
                                message: 'Voer een geldig email adres in',
                            },
                        ],
                    }">
                    <template #label="{ isInvalid }">
                        <label :class="{ 'text-danger': isInvalid }" for="form-email">
                            E-Mailadres<span class="text-danger">*</span>
                        </label>
                    </template>
                    <template #input="{ isInvalid }">
                        <input id="form-email" v-model="state.email" class="form-control"
                            :class="{ 'is-invalid': isInvalid }" type="text" placeholder="Wat is jouw e-mailadres?"
                            :disabled="state.loading" />

                    </template>
                    <template #error="{ isInvalid }">
                        <ErrorMessage v-if="isInvalid && state.submitted && state.email === ''" class="text-danger"
                            message="Dit veld is verplicht" />
                        <ErrorMessage v-if="v$.email.email.$invalid && state.submitted && state.email !== ''"
                            class="text-danger" message="Voer een geldig email adres in" />
                    </template>
                </EmailInput>

                <button class="btn btn-secondary" type="submit" :disabled="state.loading" aria-label="Inschrijven">
                    <template v-if="!state.loading">
                        Reserveer nu
                    </template>
                    <span v-else>Aan het laden...</span>
                </button>
            </form>
        </div>
    </div>
</template>